import React, { useContext, useEffect } from 'react';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import ContactServiceRequests from 'src/pages/Contact/ContactServiceRequests';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import ContactDocuments from 'src/pages/Contact/ContactDocuments';
import ContactProperties from 'src/pages/Contact/ContactProperties';
import ContactNotes from 'src/pages/Contact/ContactNotes';
import Tabs from 'src/components/Tabs';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { PATHS } from 'src/common/constants';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';
import {
  useContactServiceRequestsOrchestrator,
  UseContactServiceRequestsOrchestratorReturn,
} from './CustomHooks/ContactServiceRequests/useContactServiceRequestsOrchestrator';

interface ContactTabsProps {
  contact: any;
  counts: any;
  reFetchContactCountsFromAPI: any;
  documents: any[];
  loadingDocs: boolean;
  isSavingDoc: boolean;
  saveDocument: (
    document: DocumentFormData,
    entityId: string,
    userId: string
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<boolean>;
  loadingContact?: boolean;
  loadingCounts?: boolean;
}

const ContactTabs = ({
  contact,
  counts,
  reFetchContactCountsFromAPI,
  documents,
  loadingDocs,
  isSavingDoc,
  saveDocument,
  deleteDocument,
  loadingContact,
  loadingCounts,
}: ContactTabsProps) => {
  const branding: any = useContext(BrandingContext);

  const homeOwnerCheck: boolean =
    contact &&
    contact.roles &&
    contact.roles.length > 0 &&
    (contact.roles[0].role.name === 'Home Owner' ||
      contact.roles[0].role.name === 'Occupant');

  const contactId: string = contact?.id ?? '';

  const navigateTo: string = PATHS.CONTACTS_VIEW.replace(':id', contactId);
  const navLabel: string = [contact?.firstname, contact?.lastname]
    .filter(Boolean)
    .join(' ');

  const serviceRequestsOrchestrator: UseContactServiceRequestsOrchestratorReturn =
    useContactServiceRequestsOrchestrator({
      contactId,
    });

  const serviceRequestsCount: number =
    serviceRequestsOrchestrator.serviceRequests.length;
  const loadingServiceRequestsCount: boolean =
    serviceRequestsOrchestrator.isLoading && serviceRequestsCount === 0;

  const {
    homeownerData: homeownerProperties,
    homeownerLoading: loadingHomeownerProperties,
    fetchHomeownerProperties,
  } = useProperties();

  useEffect(() => {
    if (homeOwnerCheck && contact?.id) {
      void fetchHomeownerProperties(contact.id);
    }
  }, [homeOwnerCheck, contact?.id]);

  const renderCount = (value: any) => {
    if (loadingCounts) {
      return <Skeleton variant="text" width="1.4rem" />;
    }

    return value ? value : '0';
  };

  const renderPropertiesCount = () => {
    if (loadingHomeownerProperties && homeownerProperties === null) {
      return <Skeleton variant="text" width="1.4rem" />;
    }

    return (homeownerProperties?.length ?? 0).toString();
  };

  if (loadingContact) {
    const genericPlaceholder = <TableSkeleton />;

    const placeholderItems: any = [
      {
        label: branding.branding.text.serviceRequest + 's',
        count: <Skeleton variant="text" width="1.4rem" />,
        content: (
          <ContactServiceRequests
            orchestrator={serviceRequestsOrchestrator}
            navigateTo={navigateTo}
            navLabel={navLabel}
            persistKey={`${PAGINATION_KEYS.contactServiceRequests}-${contactId}`}
            forceLoading
          />
        ),
      },
      {
        label: 'Manuals & Documents',
        count: <Skeleton variant="text" width="1.4rem" />,
        content: genericPlaceholder,
      },
      {
        label: 'Notes',
        count: <Skeleton variant="text" width="1.4rem" />,
        content: genericPlaceholder,
      },
    ];

    return <Tabs from={'Contact'} activeTab={null} items={placeholderItems} />;
  }

  const tabItems: any = [
    {
      label: branding.branding.text.serviceRequest + 's',
      count: loadingServiceRequestsCount ? (
        <Skeleton variant="text" width="1.4rem" />
      ) : (
        serviceRequestsCount.toString()
      ),
      content: (
        <ContactServiceRequests
          orchestrator={serviceRequestsOrchestrator}
          navigateTo={navigateTo}
          navLabel={navLabel}
          persistKey={`${PAGINATION_KEYS.contactServiceRequests}-${contactId}`}
        />
      ),
    },
    {
      label: 'Manuals & Documents',
      count: renderCount(counts?.documentsCount),
      content: (
        <ContactDocuments
          contact={contact}
          documents={documents}
          loadingDocs={loadingDocs}
          isSaving={isSavingDoc}
          saveDocument={saveDocument}
          deleteDocument={deleteDocument}
        />
      ),
    },
    homeOwnerCheck
      ? {
          label: 'Properties',
          count: renderPropertiesCount(),
          content: <ContactProperties contact={contact} />,
        }
      : {},
    {
      label: 'Notes',
      count: renderCount(counts?.notesCount),
      content: (
        <ContactNotes
          contact={contact}
          reFetchContactCountsFromAPI={reFetchContactCountsFromAPI}
        />
      ),
    },
  ];

  return <Tabs from={'Contact'} activeTab={null} items={tabItems} />;
};

export default ContactTabs;
